import { useMemo, useState } from 'react'

type FormData = {
  atendente: string
  dataElaboracao: string
  tipoCaso: string
  osNumero: string
  clienteAceitaCredito: string
  possuiDebitos: string
  tratativaDebito: string
  creditoInativado: string
  clienteNome: string
  cpf: string
  mae: string
  pai: string
  nascimento: string
  naturalidade: string
  rg: string
  emissaoRg: string
  cidadeMoradia: string
  rua: string
  numero: string
  bairro: string
  motivoTermo: string
  periodo: string
  valor: string
  valorExtenso: string
  banco: string
  agencia: string
  conta: string
  testemunha1Nome: string
  testemunha1Rg: string
  testemunha2Nome: string
  testemunha2Rg: string
  observacoesInternas: string
}

type Checks = {
  contaTitular: boolean
  semCvv: boolean
  credito: boolean
  docs: boolean
  rubrica: boolean
  testemunha: boolean
}

type Risk = { type: 'pending' | 'danger' | 'warn'; text: string }

const todayISO = () => {
  const now = new Date()
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 10)
}

const initialForm = (): FormData => ({
  atendente: '',
  dataElaboracao: todayISO(),
  tipoCaso: '',
  osNumero: '',
  clienteAceitaCredito: '',
  possuiDebitos: '',
  tratativaDebito: '',
  creditoInativado: '',
  clienteNome: '',
  cpf: '',
  mae: '',
  pai: '',
  nascimento: '',
  naturalidade: '',
  rg: '',
  emissaoRg: '',
  cidadeMoradia: '',
  rua: '',
  numero: '',
  bairro: '',
  motivoTermo: '',
  periodo: '',
  valor: '',
  valorExtenso: '',
  banco: '',
  agencia: '',
  conta: '',
  testemunha1Nome: '',
  testemunha1Rg: '',
  testemunha2Nome: '',
  testemunha2Rg: '',
  observacoesInternas: '',
})

const initialChecks: Checks = {
  contaTitular: false,
  semCvv: false,
  credito: false,
  docs: false,
  rubrica: false,
  testemunha: false,
}

const requiredBase: Array<keyof FormData> = [
  'atendente', 'dataElaboracao', 'tipoCaso', 'clienteAceitaCredito', 'possuiDebitos', 'creditoInativado',
  'clienteNome', 'cpf', 'mae', 'nascimento', 'naturalidade', 'rg', 'emissaoRg',
  'cidadeMoradia', 'rua', 'numero', 'bairro', 'motivoTermo', 'periodo', 'valor',
  'banco', 'agencia', 'conta',
]

const requiredLabels: Partial<Record<keyof FormData, string>> = {
  atendente: 'Atendente responsável',
  dataElaboracao: 'Data de elaboração',
  tipoCaso: 'Motivo operacional da solicitação',
  clienteAceitaCredito: 'Cliente aceita crédito na matrícula?',
  possuiDebitos: 'Cliente possui débitos junto à concessionária?',
  tratativaDebito: 'Tratativa quando há débito',
  creditoInativado: 'Conferência do crédito automático no sistema',
  clienteNome: 'Nome completo do cliente',
  cpf: 'CPF do cliente',
  mae: 'Nome da mãe',
  nascimento: 'Data de nascimento',
  naturalidade: 'Naturalidade',
  rg: 'RG',
  emissaoRg: 'Local/órgão de emissão do RG',
  cidadeMoradia: 'Cidade de moradia e estado',
  rua: 'Rua',
  numero: 'Número',
  bairro: 'Bairro',
  motivoTermo: 'Motivo da devolução no termo',
  periodo: 'Período relacionado',
  valor: 'Valor a restituir',
  banco: 'Banco',
  agencia: 'Agência',
  conta: 'Conta corrente',
}

const checkLabels: Record<keyof Checks, string> = {
  contaTitular: 'Extrato bancário está em nome do titular/cliente.',
  semCvv: 'Não será anexada cópia de cartão contendo CVV.',
  credito: 'Foi oferecida a opção de crédito na matrícula antes da devolução em conta.',
  docs: 'Documentos obrigatórios foram conferidos: termo, extrato bancário, RG e CPF.',
  rubrica: 'Cliente deverá rubricar a primeira página e assinar o termo.',
  testemunha: 'Atendente responsável deverá assinar como testemunha na segunda página.',
}

function onlyDigits(value: string) {
  return value.replace(/\D/g, '')
}

function maskCPF(value: string) {
  const digits = onlyDigits(value).slice(0, 11)
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
}

function validateCPF(value: string) {
  const cpf = onlyDigits(value)
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false
  let sum = 0
  for (let i = 0; i < 9; i += 1) sum += Number(cpf[i]) * (10 - i)
  let d1 = 11 - (sum % 11)
  d1 = d1 >= 10 ? 0 : d1
  if (d1 !== Number(cpf[9])) return false
  sum = 0
  for (let i = 0; i < 10; i += 1) sum += Number(cpf[i]) * (11 - i)
  let d2 = 11 - (sum % 11)
  d2 = d2 >= 10 ? 0 : d2
  return d2 === Number(cpf[10])
}

function moneyToNumber(value: string) {
  const cleaned = value.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.')
  return Number.parseFloat(cleaned) || 0
}

function formatMoneyInput(value: string) {
  const digits = onlyDigits(value)
  if (!digits) return ''
  return (Number.parseInt(digits, 10) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

const unidades = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove']
const especiais = ['dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove']
const dezenas = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa']
const centenas = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos', 'oitocentos', 'novecentos']

function triadToWords(value: number) {
  if (value === 0) return ''
  if (value === 100) return 'cem'
  const c = Math.floor(value / 100)
  const rest = value % 100
  const d = Math.floor(rest / 10)
  const u = rest % 10
  const parts: string[] = []
  if (c) parts.push(centenas[c])
  if (rest < 10 && rest > 0) parts.push(unidades[rest])
  else if (rest < 20 && rest >= 10) parts.push(especiais[rest - 10])
  else if (rest >= 20) parts.push(dezenas[d] + (u ? ` e ${unidades[u]}` : ''))
  return parts.join(' e ')
}

function integerToWords(value: number) {
  let num = Math.floor(value)
  if (num === 0) return 'zero'
  const scales = [
    { sing: '', plur: '' },
    { sing: 'mil', plur: 'mil' },
    { sing: 'milhão', plur: 'milhões' },
    { sing: 'bilhão', plur: 'bilhões' },
  ]
  const triads: number[] = []
  while (num > 0) {
    triads.push(num % 1000)
    num = Math.floor(num / 1000)
  }
  const words: string[] = []
  for (let i = triads.length - 1; i >= 0; i -= 1) {
    const tri = triads[i]
    if (!tri) continue
    if (i === 1 && tri === 1) words.push('mil')
    else {
      const text = triadToWords(tri)
      const scale = scales[i] ? (tri === 1 ? scales[i].sing : scales[i].plur) : ''
      words.push([text, scale].filter(Boolean).join(' '))
    }
  }
  return words.join(' e ')
}

function moneyToWords(value: number) {
  const reais = Math.floor(value)
  const cents = Math.round((value - reais) * 100)
  let text = reais === 0 ? 'zero real' : `${integerToWords(reais)} ${reais === 1 ? 'real' : 'reais'}`
  if (cents > 0) text += ` e ${integerToWords(cents)} ${cents === 1 ? 'centavo' : 'centavos'}`
  return text.toUpperCase()
}

function formatDateBR(value: string) {
  if (!value) return ''
  const [year, month, day] = value.split('-')
  if (!year || !month || !day) return value
  return `${day}/${month}/${year}`
}

function Fill({ value, fallback }: { value: string; fallback: string }) {
  return <span className={value ? 'dvcc-fill' : 'dvcc-placeholder'}>{value || fallback}</span>
}

export function DevolucaoValoresCC() {
  const [form, setForm] = useState<FormData>(initialForm)
  const [checks, setChecks] = useState<Checks>(initialChecks)
  const [message, setMessage] = useState('')
  const [validated, setValidated] = useState(false)

  const requiredFields = useMemo(() => {
    const fields = [...requiredBase]
    if (form.possuiDebitos === 'sim') fields.push('tratativaDebito')
    return fields
  }, [form.possuiDebitos])

  const risks = useMemo<Risk[]>(() => {
    const items: Risk[] = []
    requiredFields.forEach((key) => {
      if (!form[key].trim()) items.push({ type: 'pending', text: `Campo obrigatório pendente: ${requiredLabels[key] ?? key}.` })
    })
    if (form.cpf && !validateCPF(form.cpf)) items.push({ type: 'danger', text: 'CPF inválido. Revise a identificação antes de gerar o termo.' })
    if (form.valor && moneyToNumber(form.valor) <= 0) items.push({ type: 'danger', text: 'Valor da devolução deve ser maior que zero.' })
    ;(Object.keys(checks) as Array<keyof Checks>).forEach((key) => {
      if (!checks[key]) items.push({ type: 'pending', text: `Confirmação pendente: ${checkLabels[key]}` })
    })
    if (form.clienteAceitaCredito === 'sim') items.push({ type: 'danger', text: 'Cliente aceitou crédito na matrícula. Avalie se a devolução em conta corrente ainda é aplicável.' })
    if (form.possuiDebitos === 'sim' && !form.tratativaDebito) items.push({ type: 'danger', text: 'Cliente possui débitos, mas a tratativa de abatimento não foi registrada.' })
    if (!form.osNumero) items.push({ type: 'warn', text: 'Número da OS 112002 ainda não informado. Pode ser preenchido após formalização do termo, antes do encaminhamento ao faturamento.' })
    if (!form.testemunha1Rg) items.push({ type: 'warn', text: 'RG da testemunha 1 não informado. Recomendável preencher para reduzir inconsistência documental.' })
    if (!form.testemunha2Nome || !form.testemunha2Rg) items.push({ type: 'warn', text: 'Dados da segunda testemunha estão incompletos. Confirme a exigência antes de finalizar.' })
    return items
  }, [checks, form, requiredFields])

  const pendingCount = risks.filter((item) => item.type === 'pending').length
  const dangerCount = risks.filter((item) => item.type === 'danger').length
  const warnCount = risks.filter((item) => item.type === 'warn').length
  const blockingCount = pendingCount + dangerCount
  const requiredDone = requiredFields.filter((key) => Boolean(form[key].trim())).length
  const checksDone = (Object.keys(checks) as Array<keyof Checks>).filter((key) => checks[key]).length
  const progressTotal = requiredFields.length + Object.keys(checks).length + 1
  const progressDone = requiredDone + checksDone + (validateCPF(form.cpf) ? 1 : 0)
  const progress = Math.max(0, Math.min(100, Math.round((progressDone / progressTotal) * 100)))

  const emailBody = useMemo(() => {
    const assunto = `Devolução de Valores - OS 112002${form.osNumero ? ` - ${form.osNumero}` : ''} - ${form.clienteNome || 'Cliente'}`
    return `Assunto: ${assunto}\n\nPrezados, boa tarde.\n\nEncaminho documentação para análise de devolução de valores em conta corrente.\n\nDados do processo:\n- Cliente: ${form.clienteNome || '[informar]'}\n- CPF: ${form.cpf || '[informar]'}\n- Motivo: ${form.motivoTermo || '[informar]'}\n- Período: ${form.periodo || '[informar]'}\n- Valor: ${form.valor || '[informar]'}\n- Banco: ${form.banco || '[informar]'}\n- Agência: ${form.agencia || '[informar]'}\n- Conta corrente: ${form.conta || '[informar]'}\n- OS 112002: ${form.osNumero || '[informar após abertura]'}\n\nAnexos conferidos:\n- Termo de devolução devidamente assinado;\n- Primeira página rubricada pelo cliente;\n- Extrato bancário em nome do titular;\n- RG e CPF;\n- Sem anexo de cartão contendo CVV.\n\nObservações internas:\n${form.observacoesInternas || '[sem observações]'}\n\nAtenciosamente,\n${form.atendente || '[nome da atendente]'}`
  }, [form])

  function updateField<K extends keyof FormData>(key: K, value: FormData[K]) {
    setValidated(false)
    setMessage('')
    setForm((current) => ({ ...current, [key]: value }))
  }

  function updateCPF(value: string) {
    updateField('cpf', maskCPF(value))
  }

  function updateMoney(value: string) {
    const formatted = formatMoneyInput(value)
    const number = moneyToNumber(formatted)
    setValidated(false)
    setMessage('')
    setForm((current) => ({ ...current, valor: formatted, valorExtenso: formatted ? moneyToWords(number) : '' }))
  }

  function toggleCheck(key: keyof Checks) {
    setValidated(false)
    setMessage('')
    setChecks((current) => ({ ...current, [key]: !current[key] }))
  }

  function validate() {
    setValidated(true)
    if (dangerCount > 0) {
      setMessage(`Validação encontrou ${dangerCount} impedimento(s) crítico(s) e ${pendingCount} pendência(s). Corrija antes de imprimir.`)
    } else if (pendingCount > 0) {
      setMessage(`Validação encontrou ${pendingCount} pendência(s) de preenchimento ou confirmação. Conclua antes de imprimir.`)
    } else {
      setMessage('Validação concluída: nenhum bloqueio crítico identificado. Revise a prévia antes de imprimir.')
    }
    return blockingCount === 0
  }

  function printTerm() {
    if (!validate()) return
    window.print()
  }

  async function copyEmail() {
    try {
      await navigator.clipboard.writeText(emailBody)
      setMessage('E-mail copiado.')
    } catch {
      setMessage('Não foi possível copiar automaticamente. Selecione o texto manualmente.')
    }
  }

  function clearAll() {
    if (!window.confirm('Limpar todos os dados preenchidos deste atendimento?')) return
    setForm(initialForm())
    setChecks(initialChecks)
    setValidated(false)
    setMessage('Formulário limpo. Nenhum dado foi armazenado.')
  }

  const upper = (value: string) => value.trim().toUpperCase()
  const witness1 = form.testemunha1Nome || form.atendente

  return (
    <section className="operational-tool dvcc8">
      <div className="tool-heading">
        <div>
          <p className="eyebrow">FERRAMENTA OPERACIONAL</p>
          <h2>Devolução de Valores CC</h2>
          <p className="muted">Formulário guiado para OS 112002, termo oficial, checklist documental e encaminhamento ao faturamento.</p>
        </div>
        <span className="status-chip">Dados temporários</span>
      </div>

      <div className="dvcc8-privacy"><strong>Privacidade:</strong> CPF, RG e dados bancários permanecem somente nesta tela enquanto ela estiver aberta. O Suite não grava esses campos no banco, cache ou armazenamento local.</div>

      <section className="dvcc8-overview">
        <div>
          <span className="dvcc8-overview-label">Conformidade do processo</span>
          <strong>{progress}%</strong>
          <div className="dvcc8-progress"><span style={{ width: `${progress}%` }} /></div>
        </div>
        <div className="dvcc8-overview-metrics">
          <div><strong>{pendingCount}</strong><span>pendência(s)</span></div>
          <div><strong>{dangerCount}</strong><span>impedimento(s)</span></div>
          <div><strong>{warnCount}</strong><span>atenção(ões)</span></div>
        </div>
      </section>

      <section className="dvcc8-card">
        <div className="dvcc8-card-head"><div><h3>1. Triagem do caso</h3><p>Valida se o caso pode seguir para devolução em conta corrente.</p></div><span>pré-análise</span></div>
        <div className="dvcc8-grid two">
          <label>Atendente responsável *<input value={form.atendente} onChange={(e) => updateField('atendente', e.target.value)} placeholder="Nome completo" /></label>
          <label>Data de elaboração *<input type="date" value={form.dataElaboracao} onChange={(e) => updateField('dataElaboracao', e.target.value)} /></label>
          <label>Motivo operacional da solicitação *<select value={form.tipoCaso} onChange={(e) => updateField('tipoCaso', e.target.value)}><option value="">Selecione</option><option>Pagamento em duplicidade</option><option>Cobrança de esgoto indevida</option><option>Erro de leitura</option><option>Pagamento indevido - outro motivo</option></select></label>
          <label>Número da OS 112002<input value={form.osNumero} onChange={(e) => updateField('osNumero', e.target.value)} placeholder="Informar quando disponível" /><small>A OS pode ser informada após a formalização do termo.</small></label>
          <label>Cliente aceita crédito na matrícula? *<select value={form.clienteAceitaCredito} onChange={(e) => updateField('clienteAceitaCredito', e.target.value)}><option value="">Selecione</option><option value="sim">Sim, aceita crédito</option><option value="nao">Não, solicita devolução em conta corrente</option></select></label>
          <label>Cliente possui débitos junto à concessionária? *<select value={form.possuiDebitos} onChange={(e) => updateField('possuiDebitos', e.target.value)}><option value="">Selecione</option><option value="nao">Não</option><option value="sim">Sim</option><option value="nao_verificado">Não verificado</option></select></label>
        </div>
        {form.possuiDebitos === 'sim' && <label className="dvcc8-full">Tratativa quando há débito *<select value={form.tratativaDebito} onChange={(e) => updateField('tratativaDebito', e.target.value)}><option value="">Selecione</option><option>Cliente orientado sobre abatimento preferencial do débito</option><option>Débito abatido antes da devolução</option><option>Não se aplica por decisão da área responsável</option></select></label>}
        <label className="dvcc8-full">Crédito automático foi tratado/conferido no sistema? *<select value={form.creditoInativado} onChange={(e) => updateField('creditoInativado', e.target.value)}><option value="">Selecione</option><option>Crédito inativado antes de prosseguir com devolução em conta</option><option>Solicitada inativação do crédito à área responsável</option><option>Não se aplica</option></select><small>Confira o tratamento no sistema em todos os casos antes de prosseguir. Em pagamento em duplicidade, confirme especialmente a situação do crédito automático.</small></label>
      </section>

      <section className="dvcc8-card">
        <div className="dvcc8-card-head"><div><h3>2. Dados do cliente</h3><p>Informações utilizadas para preencher o 2º acordante no termo.</p></div><span>dados pessoais</span></div>
        <div className="dvcc8-grid two">
          <label>Nome completo do cliente *<input value={form.clienteNome} onChange={(e) => updateField('clienteNome', e.target.value)} /></label>
          <label>CPF do cliente *<input inputMode="numeric" maxLength={14} value={form.cpf} onChange={(e) => updateCPF(e.target.value)} placeholder="000.000.000-00" /><small className={form.cpf ? (validateCPF(form.cpf) ? 'dvcc8-ok' : 'dvcc8-danger') : ''}>{form.cpf ? (validateCPF(form.cpf) ? 'CPF válido.' : 'CPF inválido.') : 'Será validado antes da impressão.'}</small></label>
          <label>Nome da mãe *<input value={form.mae} onChange={(e) => updateField('mae', e.target.value)} /></label>
          <label>Nome do pai<input value={form.pai} onChange={(e) => updateField('pai', e.target.value)} placeholder="Se constar" /></label>
          <label>Data de nascimento *<input type="date" value={form.nascimento} onChange={(e) => updateField('nascimento', e.target.value)} /></label>
          <label>Naturalidade *<input value={form.naturalidade} onChange={(e) => updateField('naturalidade', e.target.value)} placeholder="Cidade/UF" /></label>
          <label>RG *<input value={form.rg} onChange={(e) => updateField('rg', e.target.value)} /></label>
          <label>Local/órgão de emissão do RG *<input value={form.emissaoRg} onChange={(e) => updateField('emissaoRg', e.target.value)} placeholder="Ex.: SSP/MS" /></label>
          <label>Cidade de moradia e estado *<input value={form.cidadeMoradia} onChange={(e) => updateField('cidadeMoradia', e.target.value)} placeholder="Campo Grande/MS" /></label>
          <label>Rua *<input value={form.rua} onChange={(e) => updateField('rua', e.target.value)} /></label>
          <label>Número *<input value={form.numero} onChange={(e) => updateField('numero', e.target.value)} /></label>
          <label>Bairro *<input value={form.bairro} onChange={(e) => updateField('bairro', e.target.value)} /></label>
        </div>
      </section>

      <section className="dvcc8-card">
        <div className="dvcc8-card-head"><div><h3>3. Dados da devolução</h3><p>Motivo, período e valor refletidos na cláusula primeira.</p></div><span>financeiro</span></div>
        <div className="dvcc8-grid two">
          <label>Motivo da devolução no termo *<textarea value={form.motivoTermo} onChange={(e) => updateField('motivoTermo', e.target.value)} placeholder="Ex.: cobrança de esgoto indevida, pagamento em duplicidade..." /></label>
          <div className="dvcc8-stack"><label>Período relacionado *<input value={form.periodo} onChange={(e) => updateField('periodo', e.target.value)} placeholder="Ex.: 01/2026 a 03/2026" /></label><label>Valor a restituir *<input inputMode="numeric" value={form.valor} onChange={(e) => updateMoney(e.target.value)} placeholder="R$ 0,00" /></label><label>Valor por extenso<input value={form.valorExtenso} onChange={(e) => updateField('valorExtenso', e.target.value)} /><small>Sugerido automaticamente. Revise antes da impressão.</small></label></div>
        </div>
      </section>

      <section className="dvcc8-card">
        <div className="dvcc8-card-head"><div><h3>4. Dados bancários</h3><p>A conta deve estar obrigatoriamente em nome do titular/cliente.</p></div><span className="danger">alto risco</span></div>
        <div className="dvcc8-grid three">
          <label>Banco *<input value={form.banco} onChange={(e) => updateField('banco', e.target.value)} /></label>
          <label>Agência *<input value={form.agencia} onChange={(e) => updateField('agencia', e.target.value)} /></label>
          <label>Conta corrente *<input value={form.conta} onChange={(e) => updateField('conta', e.target.value)} /></label>
        </div>
        <div className="dvcc8-checks">
          <label><input type="checkbox" checked={checks.contaTitular} onChange={() => toggleCheck('contaTitular')} /><span><strong>Confirmei que o extrato bancário está em nome do titular/cliente.</strong><small>Sem essa confirmação, não é recomendável seguir com a devolução em conta corrente.</small></span></label>
          <label><input type="checkbox" checked={checks.semCvv} onChange={() => toggleCheck('semCvv')} /><span><strong>Confirmei que não será anexada cópia de cartão contendo CVV.</strong><small>CVV é informação financeira sensível e não deve ser coletado ou anexado.</small></span></label>
        </div>
      </section>

      <section className="dvcc8-card">
        <div className="dvcc8-card-head"><div><h3>5. Checklist de conformidade</h3><p>Confirmações necessárias antes da geração do termo.</p></div><span>bloqueio preventivo</span></div>
        <div className="dvcc8-checks">
          <label><input type="checkbox" checked={checks.credito} onChange={() => toggleCheck('credito')} /><span><strong>Ofereci ao cliente a opção de crédito na matrícula antes da devolução em conta.</strong></span></label>
          <label><input type="checkbox" checked={checks.docs} onChange={() => toggleCheck('docs')} /><span><strong>Conferi os documentos obrigatórios: termo, extrato bancário em nome do titular, RG e CPF.</strong></span></label>
          <label><input type="checkbox" checked={checks.rubrica} onChange={() => toggleCheck('rubrica')} /><span><strong>O cliente deverá rubricar a primeira página e assinar o termo.</strong></span></label>
          <label><input type="checkbox" checked={checks.testemunha} onChange={() => toggleCheck('testemunha')} /><span><strong>A atendente responsável pela elaboração deverá assinar como testemunha na segunda página.</strong></span></label>
        </div>
        <div className="dvcc8-grid two">
          <label>Testemunha 1 — nome<input value={form.testemunha1Nome} onChange={(e) => updateField('testemunha1Nome', e.target.value)} placeholder={form.atendente || 'Atendente responsável'} /></label>
          <label>Testemunha 1 — RG<input value={form.testemunha1Rg} onChange={(e) => updateField('testemunha1Rg', e.target.value)} /></label>
          <label>Testemunha 2 — nome<input value={form.testemunha2Nome} onChange={(e) => updateField('testemunha2Nome', e.target.value)} /></label>
          <label>Testemunha 2 — RG<input value={form.testemunha2Rg} onChange={(e) => updateField('testemunha2Rg', e.target.value)} /></label>
        </div>
        <label className="dvcc8-full">Observações internas<textarea value={form.observacoesInternas} onChange={(e) => updateField('observacoesInternas', e.target.value)} placeholder="Registre somente pontos relevantes da análise." /></label>
      </section>

      <section className="dvcc8-split">
        <div className="dvcc8-card">
          <div className="dvcc8-card-head"><div><h3>6. Painel de risco</h3><p>Pendências, impedimentos e pontos de atenção antes de imprimir.</p></div><span className={dangerCount ? 'danger' : pendingCount || warnCount ? 'warn' : 'ok'}>{dangerCount ? 'bloqueado' : pendingCount ? 'pendente' : warnCount ? 'atenção' : 'conforme'}</span></div>
          <div className="dvcc8-risks">{risks.length ? risks.map((risk, index) => <div className={`dvcc8-risk ${risk.type}`} key={`${risk.text}-${index}`}><i /> <span>{risk.text}</span></div>) : <div className="dvcc8-risk ok"><i /><span><strong>Sem bloqueios identificados.</strong> Revise a prévia do termo antes de imprimir.</span></div>}</div>
        </div>
        <div className="dvcc8-card">
          <div className="dvcc8-card-head"><div><h3>7. E-mail ao faturamento</h3><p>Texto padronizado após documentação completa.</p></div><span>padronização</span></div>
          <pre className="dvcc8-email">{emailBody}</pre>
          <button className="secondary-button" type="button" onClick={() => void copyEmail()}>Copiar e-mail</button>
        </div>
      </section>

      <section className="dvcc8-card dvcc8-preview-card">
        <div className="dvcc8-preview-toolbar">
          <div><strong>Prévia do termo oficial</strong><span>Campos em cinza indicam informação pendente.</span></div>
          <div><button className="secondary-button" type="button" onClick={validate}>Validar conformidade</button><button className="primary-button compact" type="button" onClick={printTerm}>Imprimir / Salvar em PDF</button><button className="secondary-button" type="button" onClick={clearAll}>Limpar formulário</button></div>
        </div>
        {message && <div className={`tool-status ${validated && blockingCount === 0 ? 'ok' : blockingCount ? 'error' : ''}`}>{message}</div>}

        <div className="dvcc-print-root">
          <article className="dvcc-page">
            <table className="dvcc-doc-head"><tbody><tr><td rowSpan={2} className="brand">ÁGUAS<br /><strong>GUARIROBA</strong></td><td rowSpan={2} className="title">TERMO DE DEVOLUÇÃO EM CONTA</td><td><strong>Referência</strong><br />AN01- IT021-COM02</td></tr><tr><td><strong>Revisão</strong><br />00</td></tr></tbody></table>
            <div className="dvcc-doc-body">
              <p>De um lado, <b>ÁGUAS GUARIROBA S.A.</b>, concessionária de serviços públicos de abastecimento de água e esgotamento sanitário do município de Campo Grande (MS), com sede administrativa na Rua Antonio Maria Coelho, 5401, Bairro Santa Fé, Campo Grande (MS), inscrita no CNPJ/MF sob o n. 04.089.570/0001-50 e no Cadastro de Contribuintes do Estado de Mato Grosso do Sul sob o n. 28.315.129-3, neste ato representada por seus representantes legais infra-assinados, doravante denominada simplesmente <b>ÁGUAS</b> ou <b>1ª ACORDANTE</b>;</p>
              <p>E, de outro lado, <Fill value={upper(form.clienteNome)} fallback="NOME DO CLIENTE" />, filho (a) de <Fill value={upper(form.mae)} fallback="NOME DA MÃE" /> e de <Fill value={upper(form.pai) || 'NÃO INFORMADO'} fallback="NOME DO PAI" />, nascido (a) no dia <Fill value={formatDateBR(form.nascimento)} fallback="DATA DE NASCIMENTO" />, na cidade de <Fill value={upper(form.naturalidade)} fallback="NATURALIDADE" />, devidamente inscrito (a) no CPF sob o n. <Fill value={form.cpf} fallback="CPF DO CLIENTE" /> e RG n. <Fill value={upper(form.rg)} fallback="NÚMERO DO RG DO CLIENTE" />, <Fill value={upper(form.emissaoRg)} fallback="LOCAL DE EMISSÃO" />, residente na <Fill value={upper(form.rua)} fallback="RUA DO CLIENTE" />, <Fill value={upper(form.numero)} fallback="NÚMERO DO CLIENTE" />, <Fill value={upper(form.bairro)} fallback="BAIRRO DO CLIENTE" />, na cidade de <Fill value={upper(form.cidadeMoradia)} fallback="CIDADE DE MORADIA E ESTADO" /> doravante denominado (a) <b>2º ACORDANTE</b>.</p>
              <p>As partes acima identificadas têm entre si, justo e acertado, nos termos do art. 840, do Código Civil Brasileiro, o presente <b>TERMO DE ACORDO PARA DEVOLUÇÃO DE VALORES EM CONTA CORRENTE</b>, que se regerá pelas cláusulas seguintes e pelas condições descritas no presente instrumento.</p>
              <p className="clause">CLÁUSULA PRIMEIRA – Do Objeto.</p>
              <p><span className="num">1.1.</span>Constitui objeto do presente Termo o pagamento, pela <b>1ª ACORDANTE</b> ao <b>2º ACORDANTE</b>, dos valores capazes de cobrir todos os valores pagos anteriormente pelo <b>2º ACORDANTE</b> em virtude de <Fill value={upper(form.motivoTermo)} fallback="INSERIR MOTIVO" /> ocorrido no período de <Fill value={upper(form.periodo)} fallback="PERÍODO" />. Abaixo valores apresentados e orçados pela concessionária.</p>
              <p><span className="num">1.1.1.</span>Declaram as partes que o valor de <Fill value={form.valor} fallback="R$ VALOR EM NÚMEROS" /> (<Fill value={upper(form.valorExtenso)} fallback="VALOR NOMINAL" />) é bastante e suficiente para cobrir todos os valores.</p>
              <p className="clause">CLÁUSULA SEGUNDA – Do Pagamento.</p>
              <p><span className="num">3.1.</span>O pagamento dos valores estipulados no item 1.1.1 deste Termo será efetuado em até 30 (trinta) dias úteis após a assinatura do presente, mediante depósito na conta bancária indicada pelo <b>2º ACORDANTE</b>, qual seja, conta corrente n. <Fill value={upper(form.conta)} fallback="CONTA CORRENTE DO CLIENTE" />, agência <Fill value={upper(form.agencia)} fallback="AGÊNCIA DO CLIENTE" />, banco <Fill value={upper(form.banco)} fallback="NOME DO BANCO" />.</p>
            </div>
            <footer>Página 1 de 2<br />DATA DE HOMOLOGAÇÃO DESTE DOCUMENTO – 13/12/2023<br />DATA DE REVALIDAÇÃO DESTE DOCUMENTO -</footer>
          </article>

          <article className="dvcc-page">
            <table className="dvcc-doc-head"><tbody><tr><td rowSpan={2} className="brand">ÁGUAS<br /><strong>GUARIROBA</strong></td><td rowSpan={2} className="title">TERMO DE DEVOLUÇÃO EM CONTA</td><td><strong>Referência</strong><br />AN01- IT021-COM02</td></tr><tr><td><strong>Revisão</strong><br />00</td></tr></tbody></table>
            <div className="dvcc-doc-body">
              <p className="clause first">CLÁUSULA TERCEIRA – Das Disposições Finais.</p>
              <p><span className="num">4.1.</span>Por ser a expressão da verdade, o <b>2º ACORDANTE</b>, nesta data, dará plena, geral e irrevogável quitação, para nada mais tendo a reclamar, em tempo algum, não restando quaisquer resquícios de ônus financeiro, obrigacional ou moral entre as partes envolvidas, em relação ao acontecimento mencionado neste termo, renunciando, expressamente, a qualquer direito de ação em virtude desta relação, não só por si, como também por seus sucessores e herdeiros, firmando o presente termo de acordo nesta data em duas vias e na presença de duas testemunhas.</p>
              <div className="dvcc-sign-date">Campo Grande (MS), <Fill value={formatDateBR(form.dataElaboracao)} fallback="____/____/______" />.</div>
              <div className="dvcc-party-title">1ª ACORDANTE</div>
              <div className="dvcc-company-sign"><div><b>Francis Moreira Faustino Yamamoto</b><br />Diretora Executiva</div><div><b>Gabriel Martins Buim</b><br />Diretor Presidente</div></div>
              <div className="dvcc-party-title">2º ACORDANTE</div>
              <div className="dvcc-customer-sign"><span />CPF n. <Fill value={form.cpf} fallback="CPF DO CLIENTE" /></div>
              <p className="dvcc-witness-title">TESTEMUNHAS:</p>
              <div className="dvcc-witnesses"><div><b>1.</b><span />Nome: <Fill value={upper(witness1)} fallback="NOME" /><br /><br />RG: <Fill value={upper(form.testemunha1Rg)} fallback="RG" /></div><div><b>2.</b><span />Nome: <Fill value={upper(form.testemunha2Nome)} fallback="NOME" /><br /><br />RG: <Fill value={upper(form.testemunha2Rg)} fallback="RG" /></div></div>
            </div>
            <footer>Página 2 de 2<br />DATA DE HOMOLOGAÇÃO DESTE DOCUMENTO – 13/12/2023<br />DATA DE REVALIDAÇÃO DESTE DOCUMENTO -</footer>
          </article>
        </div>
      </section>
    </section>
  )
}
